import { type ComponentPropsWithoutRef, type JSX, type ReactNode, useState } from 'react';

import { cn } from '../../lib/utils';
import { Paragraph } from '../ui/typography';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Icon } from '../ui/icon';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { eyeIcon, eyeOffIcon } from '../../icons';

type InputProps = ComponentPropsWithoutRef<typeof Input>;
type CheckboxProps = ComponentPropsWithoutRef<typeof Checkbox>;
type TextareaProps = ComponentPropsWithoutRef<typeof Textarea>;

// ---- FormInputField ---------------------------------------------------------

interface FormInputFieldProps extends Omit<InputProps, 'id'> {
  id: string;
  label: string;
  optionalSuffix?: string;
  required?: boolean;
  error?: string;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  suffix?: JSX.Element;
}

export function FormInputField({
  id,
  label,
  optionalSuffix,
  required,
  error,
  containerClassName,
  labelClassName,
  inputClassName,
  suffix,
  ...inputProps
}: FormInputFieldProps) {
  const inputElement = (
    <Input
      id={id}
      required={required}
      aria-describedby={error ? `${id}-error` : undefined}
      className={cn(
        'w-full rounded-[4px] px-3 py-2 text-sm text-gray-900 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-brand-primary',
        error ? 'border-red-500 hover:border-red-500' : 'border-gray-300 hover:border-gray-400',
        inputClassName
      )}
      {...inputProps}
    />
  );

  return (
    <div className={containerClassName}>
      <Label
        htmlFor={id}
        className={cn('mb-2 block text-sm font-medium text-black', labelClassName)}
      >
        {label}
        {optionalSuffix && (
          <span className="ml-1 font-normal text-gray-placeholder">{optionalSuffix}</span>
        )}
      </Label>
      {suffix ? (
        <div className="relative">
          {inputElement}
          {suffix}
        </div>
      ) : (
        inputElement
      )}
      {error && (
        <Paragraph id={`${id}-error`} className="mt-1 text-xs text-red-600">
          {error}
        </Paragraph>
      )}
    </div>
  );
}

// ---- PasswordInputField -----------------------------------------------------

export type PasswordInputFieldProps = Omit<FormInputFieldProps, 'type' | 'suffix'>;

/**
 * A `FormInputField` specialised for passwords: it owns the show/hide state and
 * renders the eye toggle as a suffix, so callers don't re-implement the toggle
 * (used by Login, Forgot/Reset password and the profile Change Password card).
 */
export function PasswordInputField(props: PasswordInputFieldProps) {
  const [visible, setVisible] = useState(false);

  return (
    <FormInputField
      {...props}
      type={visible ? 'text' : 'password'}
      suffix={
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setVisible((v) => !v)}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-gray-placeholder hover:bg-transparent hover:text-black"
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          <Icon src={visible ? eyeOffIcon : eyeIcon} size="lg" className="text-current" />
        </Button>
      }
    />
  );
}

// ---- FloatingInputField -----------------------------------------------------

interface FloatingInputFieldProps extends Omit<InputProps, 'id' | 'value' | 'onChange'> {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  containerClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  suffix?: ReactNode;
}

export function FloatingInputField({
  id,
  label,
  value,
  onChange,
  error,
  containerClassName,
  labelClassName,
  inputClassName,
  suffix,
  placeholder,
  ...inputProps
}: FloatingInputFieldProps) {
  return (
    <div className={cn('flex-1', containerClassName)}>
      <div className="relative">
        <Input
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={error ? `${id}-error` : undefined}
          placeholder={placeholder ?? ' '}
          className={cn(
            'peer h-11 rounded-[4px] px-4 pt-3 pb-1 text-sm text-black placeholder-transparent focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-black',
            error
              ? 'border-red-500 hover:border-red-500'
              : 'border-gray-placeholder hover:border-gray-placeholder',
            inputClassName
          )}
          {...inputProps}
        />
        <Label
          htmlFor={id}
          className={cn(
            'pointer-events-none absolute left-3 top-1 text-xs text-gray-placeholder',
            labelClassName
          )}
        >
          {label}
        </Label>
        {suffix && <div className="absolute right-3 top-1/2 -translate-y-1/2">{suffix}</div>}
      </div>
      {error && (
        <Paragraph id={`${id}-error`} className="mt-1 text-xs text-red-600">
          {error}
        </Paragraph>
      )}
    </div>
  );
}

// ---- FormTextareaField ------------------------------------------------------

interface FormTextareaFieldProps extends Omit<TextareaProps, 'id'> {
  id: string;
  label: string;
  optionalSuffix?: string;
  required?: boolean;
  error?: string;
  containerClassName?: string;
  labelClassName?: string;
  textareaClassName?: string;
}

export function FormTextareaField({
  id,
  label,
  optionalSuffix,
  required,
  error,
  containerClassName,
  labelClassName,
  textareaClassName,
  ...textareaProps
}: FormTextareaFieldProps) {
  return (
    <div className={containerClassName}>
      <Label
        htmlFor={id}
        className={cn('mb-2 block text-sm font-medium text-black', labelClassName)}
      >
        {label}
        {optionalSuffix && (
          <span className="ml-1 font-normal text-gray-placeholder">{optionalSuffix}</span>
        )}
      </Label>
      <Textarea
        id={id}
        required={required}
        aria-describedby={error ? `${id}-error` : undefined}
        className={cn(
          'w-full rounded-[4px] px-3 py-2 text-sm text-gray-900 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-brand-primary',
          error ? 'border-red-500 hover:border-red-500' : 'border-gray-300 hover:border-gray-400',
          textareaClassName
        )}
        {...textareaProps}
      />
      {error && (
        <Paragraph id={`${id}-error`} className="mt-1 text-xs text-red-600">
          {error}
        </Paragraph>
      )}
    </div>
  );
}

// ---- FormSelectField --------------------------------------------------------

export interface FormSelectOption {
  value: string;
  label: string;
}

export interface FormRadioOption {
  value: string;
  label: string;
}

interface FormSelectFieldProps {
  id: string;
  label: string;
  optionalSuffix?: string;
  placeholder?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: FormSelectOption[];
  required?: boolean;
  error?: string;
  containerClassName?: string;
  labelClassName?: string;
  triggerClassName?: string;
  disabled?: boolean;
}

// Radix Select forbids empty string values on SelectItem, so we use a sentinel internally.
const EMPTY_SELECT_VALUE = '__none__';

function toSelectValue(v: string) {
  return v === '' ? EMPTY_SELECT_VALUE : v;
}

function fromSelectValue(v: string) {
  return v === EMPTY_SELECT_VALUE ? '' : v;
}

export function FormSelectField({
  id,
  label,
  optionalSuffix,
  placeholder,
  value,
  onValueChange,
  options,
  error,
  containerClassName,
  labelClassName,
  triggerClassName,
  disabled,
}: FormSelectFieldProps) {
  return (
    <div className={containerClassName}>
      <Label
        htmlFor={id}
        className={cn('mb-2 block text-sm font-medium text-black', labelClassName)}
      >
        {label}
        {optionalSuffix && (
          <span className="ml-1 font-normal text-gray-placeholder">{optionalSuffix}</span>
        )}
      </Label>
      <Select
        value={toSelectValue(value)}
        onValueChange={(v) => onValueChange(fromSelectValue(v))}
        disabled={disabled}
      >
        <SelectTrigger
          id={id}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cn(
            'w-full rounded-[4px] px-3 py-2 text-sm text-gray-900 data-[placeholder]:text-gray-placeholder focus:ring-1 focus:ring-offset-0',
            error
              ? 'border-red-500 focus:ring-red-500'
              : 'border-gray-300 hover:border-gray-400 focus:ring-[var(--color-brand-primary)]',
            triggerClassName
          )}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={toSelectValue(opt.value)} value={toSelectValue(opt.value)}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && (
        <Paragraph id={`${id}-error`} className="mt-1 text-xs text-red-600">
          {error}
        </Paragraph>
      )}
    </div>
  );
}

// ---- FormRadioGroupField ----------------------------------------------------

interface FormRadioGroupFieldProps {
  id: string;
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: FormRadioOption[];
  disabled?: boolean;
  containerClassName?: string;
  legendClassName?: string;
  optionClassName?: string;
  radioClassName?: string;
  orientation?: 'horizontal' | 'vertical';
}

export function FormRadioGroupField({
  id,
  label,
  value,
  onValueChange,
  options,
  disabled,
  containerClassName,
  legendClassName,
  optionClassName,
  radioClassName,
  orientation = 'horizontal',
}: FormRadioGroupFieldProps) {
  return (
    <fieldset className={containerClassName}>
      <legend className={cn('mb-2 text-sm font-medium text-gray-700', legendClassName)}>
        {label}
      </legend>
      <RadioGroup
        value={value}
        onValueChange={onValueChange}
        disabled={disabled}
        orientation={orientation}
        className={cn(
          'flex gap-6',
          orientation === 'vertical' ? 'flex-col items-start' : 'items-center'
        )}
      >
        {options.map((option) => {
          const optionId = `${id}-${option.value}`;
          return (
            <div
              key={option.value}
              className={cn(
                'flex items-center gap-2 text-sm text-gray-700',
                disabled && 'cursor-not-allowed opacity-60',
                optionClassName
              )}
            >
              <RadioGroupItem
                id={optionId}
                value={option.value}
                className={cn('h-5 w-5 border-primary text-primary', radioClassName)}
              />
              <Label
                htmlFor={optionId}
                className={cn('cursor-pointer font-normal', disabled && 'cursor-not-allowed')}
              >
                {option.label}
              </Label>
            </div>
          );
        })}
      </RadioGroup>
    </fieldset>
  );
}

// ---- FormCheckboxField ------------------------------------------------------

interface FormCheckboxFieldProps extends Omit<CheckboxProps, 'id'> {
  id: string;
  label: string;
  containerClassName?: string;
  labelClassName?: string;
  checkboxClassName?: string;
}

export function FormCheckboxField({
  id,
  label,
  containerClassName,
  labelClassName,
  checkboxClassName,
  ...checkboxProps
}: FormCheckboxFieldProps) {
  return (
    <div className={cn('flex items-center justify-between', containerClassName)}>
      <Label htmlFor={id} className={cn('text-sm text-gray-700', labelClassName)}>
        {label}
      </Label>
      <Checkbox
        id={id}
        className={cn(
          'h-4 w-4 rounded-sm border-gray-300 data-[state=checked]:border-[var(--color-brand-primary)] data-[state=checked]:bg-[var(--color-brand-primary)] focus-visible:ring-[var(--color-brand-primary)]',
          checkboxClassName
        )}
        {...checkboxProps}
      />
    </div>
  );
}
